import React, { useState, useEffect } from 'react';
import { CreditCard, ExternalLink, CheckCircle, AlertCircle, Loader2, Link2, Unlink } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const StripeConnect = () => {
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('rooftrack_token');
      const response = await axios.get(`${BACKEND_URL}/api/stripe-connect/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (err) {
      console.error('Error checking Stripe status:', err);
      setError('Failed to check Stripe connection status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);
    try {
      const token = localStorage.getItem('rooftrack_token');
      const origin = window.location.origin;
      
      const response = await axios.post(
        `${BACKEND_URL}/api/stripe-connect/onboarding-link`,
        {
          return_url: `${origin}/dashboard/stripe?onboarding=complete`,
          refresh_url: `${origin}/dashboard/stripe?onboarding=refresh`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Error starting Stripe connection:', err);
      setError(err.response?.data?.detail || 'Failed to start Stripe connection');
      setIsConnecting(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const token = localStorage.getItem('rooftrack_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/stripe-connect/dashboard-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.url, '_blank');
    } catch (err) {
      console.error('Error opening Stripe dashboard:', err);
      setError(err.response?.data?.detail || 'Failed to open Stripe dashboard');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Stripe account? You will no longer be able to accept payments until you reconnect.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('rooftrack_token');
      await axios.delete(`${BACKEND_URL}/api/stripe-connect/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus({ connected: false });
    } catch (err) {
      console.error('Error disconnecting Stripe:', err);
      setError('Failed to disconnect Stripe account');
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading Stripe connection status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <CreditCard className="w-8 h-8 mr-3 text-purple-600" />
          Payment Setup
        </h1>
        <p className="text-gray-600 mt-2">
          Connect your Stripe account to accept payments directly from customers
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          <AlertCircle className="w-5 h-5 inline mr-2" />
          {error}
        </div>
      )}

      {/* Connection Status Card */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              status?.connected && status?.charges_enabled 
                ? 'bg-green-100' 
                : status?.connected 
                  ? 'bg-yellow-100' 
                  : 'bg-gray-100'
            }`}>
              {status?.connected && status?.charges_enabled ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : status?.connected ? (
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              ) : (
                <Link2 className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <div className="ml-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {status?.connected && status?.charges_enabled 
                  ? 'Stripe Connected' 
                  : status?.connected 
                    ? 'Setup Incomplete' 
                    : 'Not Connected'}
              </h2>
              <p className="text-gray-600">
                {status?.connected && status?.charges_enabled 
                  ? 'You can accept payments from customers' 
                  : status?.connected 
                    ? 'Complete your Stripe account setup to accept payments' 
                    : 'Connect your Stripe account to start accepting payments'}
              </p>
            </div>
          </div>
          
          {status?.connected && status?.charges_enabled && (
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              Active
            </span>
          )}
        </div>

        {/* Account Details */}
        {status?.connected && (
          <div className="border-t pt-4 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Account Status</p>
                <p className="font-semibold capitalize">{status.account_status || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payments</p>
                <p className={`font-semibold ${status.charges_enabled ? 'text-green-600' : 'text-yellow-600'}`}>
                  {status.charges_enabled ? 'Enabled' : 'Pending'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Payouts</p>
                <p className={`font-semibold ${status.payouts_enabled ? 'text-green-600' : 'text-yellow-600'}`}>
                  {status.payouts_enabled ? 'Enabled' : 'Pending'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Business Name</p>
                <p className="font-semibold">{status.business_name || 'Not set'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {!status?.connected ? (
            <Button 
              onClick={handleConnect}
              disabled={isConnecting}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect Stripe Account
                </>
              )}
            </Button>
          ) : !status?.onboarding_complete || !status?.charges_enabled ? (
            <Button 
              onClick={handleConnect}
              disabled={isConnecting}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Complete Setup
                </>
              )}
            </Button>
          ) : (
            <>
              <Button 
                onClick={handleOpenDashboard}
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Stripe Dashboard
              </Button>
              <Button 
                onClick={handleDisconnect}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Unlink className="w-4 h-4 mr-2" />
                Disconnect
              </Button>
            </>
          )}
          
          <Button onClick={checkStatus} variant="outline">
            Refresh Status
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2">How It Works</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• Connect your own Stripe account (free to create)</li>
            <li>• Payments from customers go directly to your account</li>
            <li>• Stripe handles payment processing securely</li>
            <li>• View transactions in your Stripe dashboard</li>
          </ul>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h3 className="font-semibold text-purple-900 mb-2">Fees</h3>
          <ul className="text-sm text-purple-800 space-y-2">
            <li>• <strong>Stripe fee:</strong> 2.9% + $0.30 per transaction</li>
            <li>• <strong>Platform fee:</strong> 2.9% per transaction</li>
            <li>• No monthly fees or minimums</li>
            <li>• Payouts to your bank in 2 business days</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StripeConnect;
